import React, { useEffect,useState } from "react";
import "../../../../../../css/CashflowModeller2/Cards/Cards.css";
import AddEditButton from "../../Buttons/AddEditButton";
import InputSmall from "../../Inputs/InputSmall";
import Input2 from "../../Inputs/Input2";
import { useCfmContext } from "../../../Context/CfmContext";
import { useContextUpdater } from "../../../Hooks/UseContextUpdater";
import RegularIncomeDialogBox from "../../DialogBoxes/RegularIncomeDialogBox";
import TimelineOptions from "../../../Utils/TimelineOptions";
import ArrowDown from "../../Buttons/ArrowDown";
import Input1 from "../../Inputs/Input1";

/**
 * RegularIncomeCardSmall is a React functional component that displays a summary card
 * for regular investment income within the Cashflow Modeller tool. It allows users to
 * view and (conditionally) add/edit regular income entries, with collapsible details.
 *
 * Features:
 * - Displays a list of regular income entries (start, end, amount).
 * - Provides an "Add/Edit" button to open a dialog for modifying income entries.
 * - Collapsible UI to show/hide income details.
 * - Disables editing if there are no investments present.
 * - Ensures at least one regular income entry is present.
 *
 * Context:
 * - Uses `useCfmContext` to access the global state.
 * - Uses `useContextUpdater` to update the "regularIncome" field in context.
 *
 * State:
 * - `isCollapsed`: Controls whether the card details are collapsed.
 * - `isDialogOpen`: Controls the visibility of the add/edit dialog.
 *
 * @component
 * @returns {JSX.Element} The rendered RegularIncomeCardSmall component.
 */
const RegularIncomeCardSmall = () => {
  const { state } = useCfmContext();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const regularIncome =
    state.regularIncome?.length > 0
      ? state.regularIncome
      : [{ start: "", end: "", amount: "" }];

  const { updateContextField, addItem } = useContextUpdater("regularIncome");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (regularIncome.length === 0) {
      addItem({
        start: "",
        end: "",
        amount: "",
      });
    }
  }, [regularIncome, addItem]);

  return (
    <>
      <div className="card-divider " />
      <div className="card-rows ">
        <div className="Title">Investment Income</div>
        <div className="card-header-actions">
          <div className="relative-button-wrapper">
            <AddEditButton
              onClick={() => setIsDialogOpen(true)}
              isDisabled={!(state.investments?.length > 0)}
            />
            {isDialogOpen && (
              <div className="dialog-overlay">
                <RegularIncomeDialogBox
                  onClose={() => setIsDialogOpen(false)}
                />
              </div>
            )}
          </div>
          <div
            className={`arrow-toggle ${isCollapsed ? "rotate" : ""}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ArrowDown />
          </div>
        </div>
      </div>
      <div
        className={`card-collapse-container ${isCollapsed ? "collapsed" : ""}`}
      >
        {regularIncome.map((income, index) => (
          <div key={index}>
            <div className="no-dovider-dialogBox" />
            <div key={index} className="card-rows no-label-container">
              <InputSmall
                label={index === 0 ? "Start" : ""}
                value={income.start}
                onChange={(val) => updateContextField(index, "start", val)}
                isDisabled={state.regularIncome?.length === 0}
                readOnly
              />
              <InputSmall
                label={index === 0 ? "End" : ""}
                value={income.end}
                onChange={(val) => updateContextField(index, "end", val)}
                isDisabled={state.regularIncome?.length === 0}
                readOnly
              />
              <Input1
                label={index === 0 ? "Amount" : ""}
                value={income.amount}
                onChange={(val) => updateContextField(index, "amount", val)}
                isDisabled={state.regularIncome?.length === 0}
                options={TimelineOptions}
                readOnly
                type="number"
                currency={true}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default RegularIncomeCardSmall;
